import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params, Router, RouterLink} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {lastValueFrom} from 'rxjs';
import {AdminContextService} from "@app/security/admin-context.service";
import {LoadingService} from "@ui-kit/utils/service/loading.service";
import {SnackbarService} from "@ui-kit/utils/service/snackbar.service";
import {AuthenticationService} from "@ui-kit/security/service/authentication.service";
import {VerifyOTPRequest} from "@ui-kit/security/model/user.model";
import {OneTimePinService} from "@ui-kit/security/service/one-time-pin.service";
import {UpdatePasswordService} from "@ui-kit/security/service/update-password.service";
import {NgOtpInputModule} from "ng-otp-input";
import {MatError} from "@angular/material/form-field";
import {AsyncPipe, NgIf} from "@angular/common";
import {MatIconModule} from "@angular/material/icon";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatButtonModule} from "@angular/material/button";
import {CenteredCardLayoutComponent} from "src/app/components/centered-card-layout/centered-card-layout.component";

@Component({
  selector: 'app-verify-account',
  templateUrl: './verify-account.component.html',
  styleUrls: ['./verify-account.component.scss'],
  imports: [
    NgOtpInputModule,
    MatError,
    AsyncPipe,
    MatIconModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    RouterLink,
    CenteredCardLayoutComponent,
    NgIf
  ],
  standalone: true
})
export class VerifyAccountComponent implements OnInit {

  public otpForm;
  public errMessage: string | null = null;
  public loading: boolean = false;
  public len = 0;

  @ViewChild('ngOtpInput') ngOtpInputRef: any;


  constructor(private context: AdminContextService,
              private route: ActivatedRoute,
              private fb: FormBuilder,
              private oneTimePinService: OneTimePinService,
              private passwordService: UpdatePasswordService,
              private authService: AuthenticationService,
              private router: Router,
              public loader: LoadingService,
              private utils: SnackbarService
  ) {
    this.otpForm = this.resetForm();

  }


  get userEmail(): string {
    if (this.context.currentUser) {
      return this.context.currentUser.email;
    }
    if (this.route.snapshot.paramMap.get('userEmail')) {
      return this.route.snapshot.paramMap.get('userEmail')!;
    }
    return '';
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params: Params) => {
      if (params.hasOwnProperty('sendOtp')) {
        this.onResendOTP(false);
      }
    });
  }

  onResendOTP(showPopUp: boolean = true): void {
    this.loading = true;
    this.errMessage = null;
    this.oneTimePinService.requestOtp(this.context.currentUser!.id!).subscribe(
      () => {
        this.loading = false;
        // show success message
        if (showPopUp) {
          this.utils.openSnackBar('OTP sent successfully');
        }
      },
      (err) => {
        this.loading = false;
        switch (err.status) {
          case 400:
            if ('ValidationException' === err.headers.get('error_code')) {
              this.errMessage = '' + err.headers.get('error_message');
              this.otpForm.get('pin')!.setValue(null);
              this.ngOtpInputRef.setValue(null);
            }
            break;
          default:
            this.utils.openSnackBar('An unexpected error occurred, please try again');
        }
      }
    );
  }

  onVerify() {
    const id = this.context.currentUser!.id!;
    const pin = this.otpForm.value.pin;
    const request: VerifyOTPRequest = {id: id, pin: pin};

    this.loading = true;
    this.errMessage = null;
    lastValueFrom(this.passwordService.verifyNewUserAccount(request))
      .then(
        (res) => {
          this.loading = false;
          this.authService.setToken(res);

          // set user as verified in the context
          this.authService.verifyUser();
          let returnUrl = this.route.snapshot.paramMap.get('returnUrl') ?? '/';

          this.router.navigate([returnUrl])
            .catch((error: string) => {
              console.log(error)
            });
        },
        (err) => {
          this.loading = false;
          switch (err.status) {
            case 400:
              if ('ValidationException' === err.headers.get('error_code')) {
                this.errMessage = '' + err.headers.get('error_message');
                this.otpForm.get('pin')!.setValue(null);
                this.ngOtpInputRef.setValue(null);
              }
              break;
            default:
              console.log(err);
              this.errMessage = 'An unexpected error occurred, please try again';
          }
        }
      );
  }

  onOtpChange(event: any) {
    this.otpForm.get('pin')!.setValue(event);
    this.len = this.otpForm.value.pin.toString().length;
  }

  private resetForm(): FormGroup {
    return this.fb.group({
      pin: [null, Validators.required],
    });
  }
}
